import { cn } from "@/lib/utils";
import menuItems, { ChevronDown, type MenuItem } from "@/static/menuConfig";
import { useAuthStore } from "@/store";
import { LogOut } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAllDesignationsQuery } from "@/hooks/api/designation.queries";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar";

const AppSidebar = () => {
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const { pathname, search } = useLocation();
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());

  const { data: designations = [] } = useAllDesignationsQuery();

  const dynamicMenuItems = useMemo(() => {
    return menuItems.map((item) => {
      if (item.title === "Admin") {
        const adminDesig = designations.find(
          (d) => d.name.toLowerCase() === "admin"
        );

        return {
          ...item,
          url: adminDesig
            ? `/people/staff?designation_id=${adminDesig.id}&designation_name=${adminDesig.name}`
            : item.url,
          items: undefined,
        };
      }
      return item;
    });
  }, [designations]);

  const hasPermission = (requiredPerms?: string[]) => {
    if (!requiredPerms || requiredPerms.length === 0) return true;
    return requiredPerms.some((p) => user?.permissions?.includes(p));
  };

  // Helper to check if a URL is active (exact or prefix match for detail pages)
  const isUrlActive = (url: string) => {
    if (url === "#") return false;

    const [urlPath, urlSearch] = url.split("?");

    // 1. If the url requires specific query parameters (like /people/staff?designation_id=...)
    // we enforce an exact match on pathname AND search to highlight the specific sub-item.
    if (urlSearch) {
      // Decode URI components in case search has special chars handled differently
      const decodedSearch = decodeURIComponent(search);
      const decodedTarget = decodeURIComponent(`?${urlSearch}`);
      return pathname === urlPath && decodedSearch === decodedTarget;
    }

    // 2. Exact match for regular paths
    if (pathname === url) return true;

    // 3. Prefix match for details pages (e.g. /activities/placement/123 matches /activities/placement)
    if (urlPath !== "/" && pathname.startsWith(urlPath + "/")) return true;

    return false;
  };

  // Auto-expand menu based on current pathname
  useEffect(() => {
    const titlesToOpen = new Set<string>();
    dynamicMenuItems.forEach((item) => {
      if (item.title === "People" && pathname.startsWith("/people/staff/")) {
        titlesToOpen.add("People");
      }
      if (item.items?.some((subItem) => isUrlActive(subItem.url))) {
        titlesToOpen.add(item.title);
      }
    });

    if (titlesToOpen.size > 0) {
      setOpenMenus((prev) => {
        const next = new Set(prev);
        titlesToOpen.forEach((title) => next.add(title));
        return next;
      });
    }
  }, [pathname, search]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  const toggleMenu = useCallback((title: string) => {
    setOpenMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(title)) {
        newSet.delete(title);
      } else {
        newSet.add(title);
      }
      return newSet;
    });
  }, []);

  const renderMenuItem = (item: MenuItem) => {
    if (!hasPermission(item.permissions)) return null;

    let displayItems = item.items;
    if (displayItems !== undefined) {
      displayItems = displayItems.filter((sub) =>
        hasPermission(sub.permissions)
      );
      if (displayItems.length === 0) return null;
    }

    const hasSubItems = displayItems && displayItems.length > 0;
    const isOpen = openMenus.has(item.title);

    const isChildActive = displayItems?.some((subItem) =>
      isUrlActive(subItem.url)
    );

    if (hasSubItems) {
      return (
        <SidebarMenuItem key={item.title}>
          <SidebarMenuButton
            onClick={() => toggleMenu(item.title)}
            className={cn(
              "group/menu-item",
              isChildActive && "text-primary font-semibold"
            )}
            isActive={isChildActive}
          >
            <item.icon className="h-5 w-5" />
            <span>{item.title}</span>
            <ChevronDown
              className={cn(
                "ml-auto h-4 w-4 transition-transform duration-200",
                isOpen && "rotate-180",
                isChildActive && "text-primary"
              )}
            />
          </SidebarMenuButton>
          {isOpen && (
            <SidebarMenuSub>
              {displayItems!.map((subItem) => (
                <SidebarMenuSubItem key={subItem.title}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={isUrlActive(subItem.url)}
                  >
                    <NavLink
                      to={subItem.url}
                      className={() =>
                        cn(
                          "flex w-full items-center gap-3 rounded-md px-2 py-1.5 transition-colors",
                          isUrlActive(subItem.url) && "font-medium"
                        )
                      }
                    >
                      <span>{subItem.title}</span>
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          )}
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild isActive={isUrlActive(item.url)}>
          <NavLink
            to={item.url}
            className={() =>
              cn(
                "flex w-full items-center gap-3 rounded-md px-2 py-1.5 transition-colors",
                isUrlActive(item.url) && "font-medium"
              )
            }
          >
            <item.icon className="h-5 w-5" />
            <span>{item.title}</span>
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar className="border-r">
      <SidebarHeader className="border-b px-6 py-5">
        <h1 className="text-primary text-xl font-bold">Enturn Recruiter</h1>
      </SidebarHeader>

      <SidebarContent className="px-3 py-4">
        <SidebarGroup>
          <SidebarGroupLabel className="sr-only">
            Navigation Links
          </SidebarGroupLabel>
          <SidebarMenu className="space-y-1">
            {dynamicMenuItems.map((item) => renderMenuItem(item))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t p-4">
        <SidebarMenuButton
          onClick={handleLogout}
          className="text-destructive hover:bg-destructive/10 hover:text-destructive w-full justify-start"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Logout
        </SidebarMenuButton>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
